import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import './TeamStats.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const TeamStats = ({ userData, hasPermission }) => {
  const [combinedStats, setCombinedStats] = useState([]);
  const [totalTeamCount, setTotalTeamCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberCases, setMemberCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [selectedImmigration, setSelectedImmigration] = useState(null);
  const [memberImmigrations, setMemberImmigrations] = useState([]);
  const [loadingImmigrations, setLoadingImmigrations] = useState(false);

  const canViewStats =
    hasPermission("view_whitelist_stats") ||
    hasPermission("manage_support") ||
    hasPermission("view_team_management") ||
    userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER;

  const dateHeaders = [
    "Heute",
    "Gestern",
    "Vor 2 Tagen",
    "Vor 3 Tagen",
    "Vor 4 Tagen",
    "Vor 5 Tagen",
    "Vor 6 Tagen",
  ];

  const mergeStats = (whitelistStats = [], supportStats = []) => {
    const merged = {};

    // 1. Immigration Stats verarbeiten (ehemals Whitelist)
    (whitelistStats || []).forEach((stat) => {
      if (!stat.user_id) return;
      merged[stat.user_id] = {
        user_id: stat.user_id,
        user_name: stat.user_name || "Unbekannt",
        daily_immigration: Array.isArray(stat.daily) ? [...stat.daily] : new Array(7).fill(0),
        daily_support: new Array(7).fill(0),
        total_immigration: stat.total_entries || 0,
        total_cases: 0,
        solved_cases: 0,
      };
    });

    (supportStats || []).forEach((stat) => {
      if (!stat.user_id) return;

      if (!merged[stat.user_id]) {
        merged[stat.user_id] = {
          user_id: stat.user_id,
          user_name: stat.user_name || "Unbekannt",
          daily_immigration: new Array(7).fill(0),
          daily_support: new Array(7).fill(0),
          total_immigration: 0,
          total_cases: 0,
          solved_cases: 0,
        };
      }

      // Daten zuweisen
      merged[stat.user_id].total_cases = stat.total_cases || 0;
      merged[stat.user_id].solved_cases = stat.solved_cases || 0;

      // SICHERSTELLEN: Wenn stat.daily existiert, kopiere es in daily_support
      if (Array.isArray(stat.daily)) {
        merged[stat.user_id].daily_support = [...stat.daily];
      }
    });

    return Object.values(merged).sort(
      (a, b) => (b.total_cases + b.total_immigration) - (a.total_cases + a.total_immigration)
    );
  };

  const fetchCombinedStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("discord_token");

      // HIER GEÄNDERT: Statt whitelist-stats nun immigration/stats
      const [immigrationRes, supportRes, guildMembersRes] = await Promise.all([
        axios.get(`${API_BASE}/api/immigration/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE}/api/support-cases/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE}/api/guild-members`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { members: [] } })),
      ]);

      const iData = immigrationRes.data.stats || [];
      const sData = supportRes.data.stats || [];
      const allGuildMembers = guildMembersRes.data?.members || [];

      // Erstelle ein Mapping von User-ID zu Server-Anzeigename
      const nameMap = {};
      allGuildMembers.forEach((m) => {
        const id = m.id || (m.user && m.user.id);
        const name =
          m.nick ||
          (m.user && (m.user.global_name || m.user.username)) ||
          m.username ||
          m.global_name;
        if (id) nameMap[id] = name;
      });

      const merged = mergeStats(iData, sData);

      // Namen durch Server-Anzeigenamen ersetzen, falls vorhanden
      const updatedStats = merged.map((stat) => ({
        ...stat,
        user_name: nameMap[stat.user_id] || stat.user_name,
      }));

      setCombinedStats(updatedStats);

      // 3. Team Member Gesamtanzahl von Discord holen (wie TeamDashboard.js)
      const teamRoleId = process.env.REACT_APP_TEAM_ROLE_ID;
      const teamCount = allGuildMembers.filter(
        (member) => member.roles && member.roles.includes(teamRoleId)
      ).length;
      setTotalTeamCount(teamCount);
    } catch (err) {
      console.error("Fehler beim Laden der Stats:", err);
      setError(err.response?.data?.error || "Server-Verbindungsfehler");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewStats) fetchCombinedStats();
  }, [canViewStats, fetchCombinedStats]);

  const getSuccessRate = (solved, total) =>
    total === 0 ? 0 : Math.round((solved / total) * 100);

  const fetchMemberCases = async (userId, userName) => {
    try {
      setLoadingCases(true);
      const token = localStorage.getItem("discord_token");
      const response = await axios.get(`${API_BASE}/api/support-cases/reporter/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMemberCases(response.data.cases || []);
        setSelectedMember({ id: userId, name: userName });
      }
    } catch (err) {
      console.error("Fehler beim Laden der Support-Fälle:", err);
    } finally {
      setLoadingCases(false);
    }
  };

  const closeModal = () => {
    setSelectedMember(null);
    setMemberCases([]);
  };

  const fetchMemberImmigrations = async (userId, userName) => {
    try {
      setLoadingImmigrations(true);
      const token = localStorage.getItem("discord_token");
      const response = await axios.get(`${API_BASE}/api/immigration/teamler/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMemberImmigrations(response.data.entries || []);
        setSelectedImmigration({ id: userId, name: userName });
      }
    } catch (err) {
      console.error("Fehler beim Laden der Einreisen:", err);
    } finally {
      setLoadingImmigrations(false);
    }
  };

  const closeImmigrationModal = () => {
    setSelectedImmigration(null);
    setMemberImmigrations([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const caseTypesList = {
    BugReport: "Bug Report (Spiel/Server Fehler)",
    SaltyChat: "SaltyChat Problem/Fehler",
    PlayerReport: "Spieler Report (Regelverstoß)",
    TechIssue: "Technisches Problem (Client/Hardware)",
    Other: "Anderes/Sonstiges",
  };

  const statusInfoMap = {
    open: { text: "⚠️ Offen", class: "team-stats-pill open" },
    solved: { text: "✅ Gelöst", class: "team-stats-pill solved" },
  };

  if (!canViewStats) {
    return (
      <div className="team-stats-container">
        <div className="dashboard-main">
          <p className="team-access-denied">Zugriff verweigert</p>
        </div>
      </div>
    );
  }

  const totalSupportCases = combinedStats.reduce((a, b) => a + (b.total_cases || 0), 0);
  const totalImmigration = combinedStats.reduce((a, b) => a + (b.total_immigration || 0), 0);

  return (
    <div className="team-stats-container">
      <div className="dashboard-main">
        {/* Hero Panel */}
        <div className="team-stats-hero">
          <h2>Team Statistik der letzen 7 Tagen</h2>
          <div className="team-stats-pills">
            <div className="team-stat-pill purple">
              <small>Aktive Teammitglieder im Support</small>
              <h4>{combinedStats.length} / {totalTeamCount}</h4>
            </div>
            <div className="team-stat-pill blue">
              <small>Support Gesamt</small>
              <h4>{totalSupportCases}</h4>
            </div>
            <div className="team-stat-pill green">
              <small>Einreisen Gesamt</small>
              <h4>{totalImmigration}</h4>
            </div>
          </div>
        </div>

        {/* Stats Table Card */}
        <div className="team-stats-card">
          <div className="team-stats-body">
            {loading ? (
              <div className="team-loading">
                <div className="spinner"></div>
                <h2>Statistiken werden geladen...</h2>
              </div>
            ) : error ? (
              <div className="team-error-banner">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            ) : combinedStats.length === 0 ? (
              <div className="team-empty">
                In den letzen 7 Tagen wurden keine Support-Fälle oder Einreisen bearbeitet.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="team-stats-table">
                  <thead>
                    <tr>
                      <th>Teammitglied</th>
                      <th>Support-Fälle</th>
                      <th>Einreisen</th>
                      {dateHeaders.map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {combinedStats.map((stat) => (
                      <tr
                        key={stat.user_id}
                        onClick={() => fetchMemberCases(stat.user_id, stat.user_name)}
                        className="team-stats-row-clickable"
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <div className="team-member-cell">
                            <span className="team-member-name">{stat.user_name}</span>
                            <span className="team-member-id">{stat.user_id}</span>
                          </div>
                        </td>
                        <td>
                          <div className="team-stats-value">
                            <span className="team-value-primary">{stat.total_cases}</span>
                            <span className="team-value-secondary">
                              {getSuccessRate(stat.solved_cases, stat.total_cases)}% gelöst
                            </span>
                          </div>
                        </td>
                        <td onClick={(e) => { e.stopPropagation(); fetchMemberImmigrations(stat.user_id, stat.user_name); }} style={{ cursor: 'pointer' }}>
                          <div className="team-stats-value">
                            <span className="team-value-success">{stat.total_immigration}</span>
                            <span className="team-value-secondary">Einträge</span>
                          </div>
                        </td>
                        {/* ABSICHERUNG: Fallback auf leeres Array falls daily_support undefined ist */}
                        {(stat.daily_support || new Array(7).fill(0)).map(
                          (sCount, idx) => {
                            const iCount = (stat.daily_immigration || [])[idx] || 0;
                            const hasActivity = sCount > 0 || iCount > 0;
                            return (
                              <td
                                key={idx}
                                className={`team-daily-cell ${hasActivity ? 'active' : ''}`}
                              >
                                <div className="team-daily-items">
                                  <span className={`team-daily-value support ${sCount > 0 ? '' : 'inactive'}`}>
                                    {sCount}
                                  </span>
                                  <div className="team-daily-separator"></div>
                                  <span className={`team-daily-value immigration ${iCount > 0 ? '' : 'inactive'}`}>
                                    {iCount}
                                  </span>
                                </div>
                              </td>
                            );
                          },
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für Support-Fälle eines Teamlers */}
      {selectedMember && (
        <div className="team-stats-modal-overlay" onClick={closeModal}>
          <div className="team-stats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="team-stats-modal-header">
              <h3>Support-Fälle von {selectedMember.name}</h3>
              <button className="team-stats-modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="team-stats-modal-body">
              {loadingCases ? (
                <div className="team-loading">
                  <div className="spinner"></div>
                  <h3>Support-Fälle werden geladen...</h3>
                </div>
              ) : memberCases.length === 0 ? (
                <div className="team-empty">
                  Keine Support-Fälle für dieses Teammitglied gefunden.
                </div>
              ) : (
                <div className="team-stats-cases-list">
                  {memberCases.map((case_) => (
                    <div key={case_.id} className="team-stats-case-item">
                      <div className="team-stats-case-header">
                        <span className="team-stats-case-id">#{case_.id}</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {case_.solved_by === selectedMember.id && case_.reporter_user_id !== selectedMember.id && (
                            <span className="team-stats-pill solved-by">✅ Gelöst</span>
                          )}
                          <span className={statusInfoMap[case_.status]?.class || "team-stats-pill"}>
                            {statusInfoMap[case_.status]?.text || case_.status}
                          </span>
                        </div>
                      </div>
                      <div className="team-stats-case-content">
                        <p>
                          <strong>Fall-Typen:</strong>{" "}
                          {case_.case_types
                            .split(",")
                            .map((typeKey) => caseTypesList[typeKey])
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        <p>
                          <strong>Betroffener:</strong> {case_.subject_username} ({case_.subject_discord_id})
                        </p>
                        <p>
                          <strong>Kommentar:</strong> {case_.comment}
                        </p>
                        {case_.solve_comment && (
                          <p style={{ color: "var(--success-color)" }}>
                            <strong>Lösung:</strong> {case_.solve_comment}
                          </p>
                        )}
                      </div>
                      <div className="team-stats-case-footer">
                        <span>{formatDate(case_.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal für Einreisen eines Teamlers */}
      {selectedImmigration && (
        <div className="team-stats-modal-overlay" onClick={closeImmigrationModal}>
          <div className="team-stats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="team-stats-modal-header">
              <h3>Einreisen von {selectedImmigration.name}</h3>
              <button className="team-stats-modal-close" onClick={closeImmigrationModal}>
                ✕
              </button>
            </div>
            <div className="team-stats-modal-body">
              {loadingImmigrations ? (
                <div className="team-loading">
                  <div className="spinner"></div>
                  <h3>Einreisen werden geladen...</h3>
                </div>
              ) : memberImmigrations.length === 0 ? (
                <div className="team-empty">
                  Keine Einreisen für dieses Teammitglied gefunden.
                </div>
              ) : (
                <div className="team-stats-cases-list">
                  {memberImmigrations.map((entry) => (
                    <div key={entry.id} className="team-stats-case-item">
                      <div className="team-stats-case-header">
                        <span className="team-stats-case-id">#{entry.id}</span>
                        <span className="team-stats-pill immigration">Einreise</span>
                      </div>
                      <div className="team-stats-case-content">
                        <p>
                          <strong>InGame ID:</strong> {entry.player_id}
                        </p>
                        <p>
                          <strong>FiveM Name:</strong> {entry.player_name || "Nicht angegeben"}
                        </p>
                      </div>
                      <div className="team-stats-case-footer">
                        <span>{formatDate(entry.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamStats;
